import React from 'react';

/**
 * Whether the model named the right thing, on the row where it named it.
 *
 * The run header reports one identification percentage. This is the same score
 * per answer, so the misses can be found and read rather than inferred from an
 * average — and the two things being compared are printed underneath, because
 * a verdict is only actionable next to the claim it was graded against.
 *
 * Five states, kept distinct on purpose:
 *   scored + graded   brand ✓ / ✗
 *   scored, no brand  the manifest names none, or the run was never asked for
 *                     one, so brand is not graded ("Brand -")
 *   graded elsewhere  the lot was graded on another of its answers — the item
 *                     closest to the manifest — so this one shows no badge
 *   not scored        a multi-item pallet or a blank line; muted, never a miss
 *   unavailable       the run's dataset has no manifest at all, so nothing was
 *                     ever graded — no badge, rather than a "not scored" chip
 *                     on every row blaming a line nobody read
 */
const IdentificationBadge = ({ identification, formatTooltip }) => {
  if (!identification || identification.available === false) return null;
  if (identification.gradedElsewhere) return null;
  const tooltip = typeof formatTooltip === 'function' ? formatTooltip(identification) : undefined;

  if (!identification.scored) {
    return (
      <span
        className="inline-flex rounded bg-surface-2 px-1 py-0.5 text-[9px] font-semibold text-muted"
        title={tooltip}
      >
        Not scored
      </span>
    );
  }

  const { brandCorrect, nameF1, modelBrand, expectedBrand, expectedName } = identification;
  const brandClass =
    brandCorrect === true
      ? 'bg-positive/10 text-positive'
      : brandCorrect === false
        ? 'bg-danger/10 text-danger'
        : 'bg-surface-2 text-muted';
  const brandLabel = brandCorrect === true ? '✓' : brandCorrect === false ? '✗' : '-';
  const nameClass =
    nameF1 === null
      ? 'bg-surface-2 text-muted'
      : nameF1 >= 0.75
        ? 'bg-positive/10 text-positive'
        : nameF1 >= 0.4
          ? 'bg-warn/10 text-warn'
          : 'bg-danger/10 text-danger';

  return (
    <div className="space-y-0.5" title={tooltip}>
      <span className="inline-flex flex-wrap items-center gap-1">
        <span className={`inline-flex rounded px-1 py-0.5 text-[9px] font-semibold ${brandClass}`}>
          Brand {brandLabel}
        </span>
        <span className={`inline-flex rounded px-1 py-0.5 text-[9px] font-semibold ${nameClass}`}>
          Name {nameF1 === null ? '-' : nameF1.toFixed(2)}
        </span>
      </span>

      {/* An empty model brand on a graded row is a miss: the model was asked
          and named none. Left ungraded while the manifest names one, the run
          predates the prompt asking, so there was nothing to record. */}
      <div className="text-[9px] leading-snug text-muted">
        <span className="font-semibold">Brand:</span>{' '}
        {modelBrand || (
          <span className="italic">
            {brandCorrect === null && expectedBrand ? 'not recorded' : 'none given'}
          </span>
        )}
        {' · manifest: '}
        {expectedBrand || <span className="italic">none named</span>}
      </div>

      {/* The manifest line is lot-level truth. It prints on the row the lot
          was graded on, the only one in the lot that carries a verdict. */}
      {expectedName ? (
        <div className="text-[9px] leading-snug text-muted">
          <span className="font-semibold">Manifest:</span> {expectedName}
        </div>
      ) : null}
    </div>
  );
};

export default IdentificationBadge;
